import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { FileIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMS } from 'src/components/files/onedrive/context/MSContext';
import { mimetypeToIcon } from 'src/components/files/onedrive/lib/icons';
import { Checkbox } from 'src/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { getDriveItems } from 'src/graph';
import { nl } from 'date-fns/locale';
import AddToDrive from './add-to-drive';
import UploadToFitdoc from './upload-to-fitdoc';
import { RemoveFromDrive } from './remove-from-drive';

type P = {
    doctypeCollection:any
    refetchDoctypesCollection:()=>void
}

type TR_P = {
    selected_documents: string[]
    file: any
    toggleSelect: (id:string) => void
}

const TR = ({selected_documents, file, toggleSelect}:TR_P) => {

    function bytesToSize(bytes: number): string {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes === 0) return '0 Byte';
        const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString());
        return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
    }

    function formatTimestamp(timestamp:string) {
        return formatDistanceToNow(parseISO(timestamp), { addSuffix: true, locale:nl });
    }

    return (
        <TableRow className={`group ${selected_documents.includes(file.id) && '!bg-primary/10'}`}>
            <TableCell><div className='flex items-center'><Checkbox onCheckedChange={(v)=>{toggleSelect(file.id)}} checked={selected_documents.includes(file.id)} className={`${!selected_documents.includes(file.id) && 'opacity-0'} group-hover:opacity-100`}/></div></TableCell>
            <TableCell className='whitespace-nowrap max-w-10'>
            <div className='flex items-center text-neutral-500'>
                {mimetypeToIcon(file.file?.mimeType || 'folder')}
            </div>
            </TableCell>
            <TableCell onClick={()=>{window.open(file.webUrl)}} className='whitespace-nowrap cursor-pointer hover:underline'>{file.name}</TableCell>
            <TableCell className='whitespace-nowrap first-letter:uppercase text-neutral-500'>{formatTimestamp(file.lastModifiedDateTime)}</TableCell>
            <TableCell className='whitespace-nowrap text-neutral-500'>{file.lastModifiedBy.user.displayName}</TableCell>
            <TableCell className='text-neutral-500'>{bytesToSize(file.size)}</TableCell>
        </TableRow>
    )
}

const DocumentsTable = ({doctypeCollection, refetchDoctypesCollection}:P) => {
    const { accessToken } = useMS()

    const [selected_documents, setselected_documents] = useState<string[]>([]);

    const { data:documents, refetch:getDocuments } = useQuery({
        queryKey: [`items-${doctypeCollection.id}`],
        queryFn: async () => {
            try {
                setselected_documents([])
                const items = await getDriveItems(accessToken, doctypeCollection.id)
                return items.value
            } catch (error) {
                console.log(error);
                return null
            }
        },
        enabled: !!doctypeCollection
    })

    function toggleSelect(id:string) {
        setselected_documents(p=>{
            let u = [...p]
            const i = u.indexOf(id)
            if(i<0){ u.push(id) }
            else{ u.splice(i, 1) }
            return u
        })
    }

  return (
    <>
    <div className='border rounded-lg p-1 flex items-center justify-between gap-1'>
        <div className='flex items-center gap-1'>
            <RemoveFromDrive selected_documents={selected_documents} onSuccess={()=>{refetchDoctypesCollection(); setselected_documents([]); getDocuments()}}/>
            <UploadToFitdoc selected_documents={selected_documents} documents={documents} doctypeCollection={doctypeCollection} onSuccess={()=>{refetchDoctypesCollection(); setselected_documents([]); getDocuments()}}/>
        </div>
        <div>
            <AddToDrive folderId={doctypeCollection.id} onSuccess={()=>{refetchDoctypesCollection(); setselected_documents([]); getDocuments()}}/>
        </div>
    </div>

    <div className='border rounded-lg overflow-auto h-full'>
        <Table>
            <TableHeader><TableRow className={`group`}>
                <TableHead className='w-8'></TableHead>
                <TableHead><FileIcon className='w-4 h-4' strokeWidth={1}/></TableHead>
                <TableHead>Naam</TableHead>
                <TableHead>Gewijzigd</TableHead>
                <TableHead>Gewijzigd door</TableHead>
                <TableHead>Bestandsgrootte</TableHead>
            </TableRow></TableHeader>
            <TableBody>
            {Array.isArray(documents) && documents.map((file,i)=>(
                <TR key={`tr-${i}`} file={file} selected_documents={selected_documents} toggleSelect={toggleSelect}/>
            ))}
            </TableBody>
        </Table>
    </div>
    </>
  )
}

export default DocumentsTable